// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A SESSÃO NO MAPA — quem sou, o que posso, e sair
// ============================================================================
//
// 📗 Cliente, 2026-08-10: *«tem de existir um botão no mapa de login e logout com o perfil do
//    utilizador, permissões e no futuro idioma etc.»*
//
// ⚠️⚠️ **NÃO HAVIA COMO SAIR.** A porta existia — `POST /api/auth/sair` está escrita, testada
//    e termina a sessão na base — e **nenhum ecrã a chamava**. Verificado por busca: a única
//    ocorrência de `auth/sair` em `apps/web` era num teste. 📌 É a família desta semana pela
//    sétima vez: a estrutura pronta e o elo a menos. E aqui o preço é de segurança e não de
//    conforto — num posto partilhado, quem não pode sair fica com a sessão do anterior, e o
//    cookie vale 12 h.
//
// ⚠️ **E o PERFIL não estava em lado nenhum.** O `App.tsx` recebia o `papel` e usava-o para
//    esconder botões — mas quem está a olhar para o ecrã não tinha como saber **quem é** que
//    está autenticado nem **porque é que** um botão não aparece. 📌 Esconder sem explicar é a
//    família «recusar o que se mostra, sem mostrar porquê», que este projecto já corrigiu
//    duas vezes noutros sítios.
//
// ⚠️⚠️ **O IDIOMA APARECE E ESTÁ DESACTIVADO, com a razão.** Foi pedido «no futuro», e a
//    tentação era não o pôr. 📌 Mas as duas alternativas más são pôr um selector que não faz
//    nada (e quem o usar conclui que o sistema está avariado) e não pôr nada (e quem o pediu
//    não sabe se foi registado). Um controlo desactivado que **diz o que falta** é a única das
//    três que informa: a aplicação tem ~1 400 cadeias de texto em português nos componentes,
//    e traduzir exige extraí-las primeiro — é trabalho declarado, não um interruptor.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { IconeFechar } from './icones.js';
import { BotaoActualizar } from './BotaoActualizar.js';
import { cantoInicial, useArrastavel, useEscape } from './janela.js';
import { escreverNaRede } from './rede.js';

/** O que `/api/auth/sessao` devolve, na parte que este ecrã usa. */
/**
 * ⚠️⚠️ **MUDAR A PRÓPRIA SENHA — e sem isto a temporária era permanente (2026-08-18).**
 *
 * 📗 Cliente: *«um colega meu quer criar conta, como faz?»* — o superadmin cria a conta com uma
 * senha gerada e entrega-a em mão. 📌 Nesse estado **duas pessoas** sabem a senha de uma conta, o
 * que quebra a única coisa que uma senha garante: que quem a usa é quem a escolheu.
 *
 * ⚠️ **Aparece SEMPRE**, e não só quando a senha é temporária: quem quer mudar a senha por outra
 * razão qualquer não tem outro sítio onde o fazer. O que muda com `temporaria` é o **aviso**.
 */
export function MudarSenha({
  temporaria,
  abertoDeInicio = false,
  aoMudar,
}: {
  readonly temporaria: boolean;
  /** Abre o formulário logo — no ecrã de entrada da senha temporária não há mais nada a fazer. */
  readonly abertoDeInicio?: boolean;
  /**
   * Chamado depois de o servidor aceitar a senha nova. 🔴 2026-10-21 · é por aqui que o ecrã de
   * entrada (`Entrada.tsx`) relê a sessão e passa ao mapa: com a senha temporária o servidor
   * recusa tudo o resto, e a mesma lógica (e a mesma regra dos 12) serve as duas portas.
   */
  readonly aoMudar?: () => void;
}) {
  const [aberto, definirAberto] = useState(abertoDeInicio);
  const [actual, definirActual] = useState('');
  const [nova, definirNova] = useState('');
  const [aGravar, definirAGravar] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [feito, definirFeito] = useState(false);

  const gravar = async () => {
    definirAGravar(true);
    definirErro(null);
    try {
      const r = await fetch('/api/auth/senha', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ senhaActual: actual, senhaNova: nova }),
      });
      if (!r.ok) {
        // ⚠️ A razão vem do SERVIDOR: «a senha actual não coincide» é accionável, «erro» não é.
        const c = (await r.json().catch(() => null)) as { detalhe?: string } | null;
        definirErro(c?.detalhe ?? `o servidor respondeu ${r.status}`);
        return;
      }
      definirFeito(true);
      definirActual('');
      definirNova('');
      aoMudar?.();
    } catch (e) {
      definirErro(e instanceof Error ? e.message : String(e));
    } finally {
      definirAGravar(false);
    }
  };

  return (
    <div className="sessao-senha">
      {/* ⚠️⚠️ **O AVISO DA SENHA TEMPORÁRIA ESTÁ SEMPRE À VISTA, fora do formulário.** Escondê-lo
          dentro de uma secção que se abre a pedido fazia com que quem não a abrisse nunca soubesse
          — e é precisamente essa pessoa que precisa de saber. */}
      {temporaria && !feito ? (
        <p className="sessao-aviso-senha">
          ⚠️ <strong>A sua senha é temporária</strong> — foi escolhida por quem criou a conta, logo{' '}
          <strong>duas pessoas</strong> a sabem. Mude-a agora.
        </p>
      ) : null}
      {feito ? (
        <p className="sessao-senha-ok">
          ✅ Senha alterada. ⚠️ A sessão actual continua válida; nas próximas entradas use a nova.
        </p>
      ) : !aberto ? (
        <button
          type="button"
          className={temporaria ? 'btn btn--destaque' : 'btn btn--micro'}
          onClick={() => definirAberto(true)}
        >
          Mudar a minha senha
        </button>
      ) : (
        <>
          <label className="convidar-campo">
            <span>Senha actual</span>
            {/* ⚠️ **A actual é exigida mesmo com sessão válida.** Uma sessão roubada não deve
                poder trancar o dono fora da própria conta. */}
            <input
              type="password"
              value={actual}
              onChange={(e) => definirActual(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label className="convidar-campo">
            <span>Senha nova (mínimo 12 caracteres)</span>
            <input
              type="password"
              value={nova}
              onChange={(e) => definirNova(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          {/* ⚠️ **O comprimento é a ÚNICA exigência**, e é deliberado: regras de composição
              («um símbolo, um número») produzem senhas piores e previsíveis — NIST SP 800-63B,
              desde 2017. Uma frase longa vale mais do que oito caracteres com símbolos. */}
          <p className="menu-nota">
            Uma frase que só você saiba vale mais do que oito caracteres com símbolos.
          </p>
          {erro !== null ? <p className="convidar-erro">{erro}</p> : null}
          <div className="convidar-acoes faixa-quebra">
            <button
              type="button"
              className="btn btn--destaque"
              disabled={aGravar || actual === '' || nova.length < 12}
              onClick={() => void gravar()}
            >
              {aGravar ? 'a gravar…' : 'Mudar a senha'}
            </button>
            <button type="button" className="btn" onClick={() => definirAberto(false)}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export interface PerfilDaSessao {
  readonly autenticado: boolean;
  readonly utilizador: {
    readonly id: string;
    readonly nome: string;
    readonly email: string | null;
    readonly papel: string;
    readonly metodoEntrada?: string | null;
    /**
     * ⚠️⚠️ **O plano que esta sessão está a ver — e é AQUI que o servidor o põe** (D-2).
     *
     * 📗 Cliente, 2026-08-21: *«se voltar lá depois não me aparece que tenho algo
     * seleccionado»*.
     *
     * ⚠️⚠️ **Estava declarado no TOPO do perfil, onde o servidor nunca o manda.** A rota
     * devolve `utilizador: { ...u }` e o `u` traz o `planoActivo`; o ecrã lia
     * `perfil.planoActivo` — que era **sempre `undefined`**. E porque o campo estava declarado
     * **opcional**, o compilador nunca reclamou.
     *
     * 📌 É a terceira instância da mesma família neste projecto (o `travoes?` e o
     * `bloqueios?`): *«um campo opcional que ninguém preenche compila, passa nos testes, e é
     * `undefined` em produção»*. ⚠️ **Por isso a correcção não é só ler do sítio certo — é
     * APAGAR a declaração do topo.** Enquanto ela existisse, o próximo a escrever
     * `perfil.planoActivo` voltava a apanhar `undefined` sem aviso nenhum.
     *
     * ⚠️ **Continua opcional, e agora com razão:** com `utilizador: null` (sem sessão) não há
     * plano nenhum, e um servidor anterior a esta versão não o manda.
     */
    readonly planoActivo?: string | null;
  } | null;
  readonly planos: readonly {
    readonly plano: string;
    readonly descricao: string;
    readonly papel: string;
    readonly capacidades: readonly string[];
  }[];
  /**
   * ⚠️⚠️ **A senha foi escolhida por OUTRA pessoa?** `true` numa conta criada pelo superadmin,
   * até o próprio a mudar. 📌 Vem do servidor porque uma convenção na cabeça de quem criou a
   * conta não é uma guarda: ninguém a pode verificar, e o colega não tem como saber.
   */
  readonly senhaTemporaria?: boolean;
  /**
   * ⚠️⚠️ **A memória do plano existia e NÃO valeu — e porquê** (migração 079, 2026-09-10).
   *
   * 📗 Cliente: *«quando eu faço logout e login o plano fica sem escolher, tem de manter o
   * que tinha selecionado»*. A entrada restaura-o; isto é o caso em que **não** restaurou,
   * porque o plano foi apagado, foi arquivado, ou a pessoa perdeu o papel nele.
   *
   * ⚠️ **Cair em «escolher» sem dizer porquê é indistinguível de nunca ter escolhido** — e as
   * acções que resolvem são diferentes: reactivar o plano em «Gestão → Planos», ou pedir
   * papel ao superadmin. 📌 A `frase` vem do servidor, que é quem sabe qual das três é.
   *
   * ⚠️ Opcional porque um servidor anterior a esta versão não o manda, e `null` é o caso
   * normal — a maioria das entradas restaura ou nunca escolheu nada.
   */
  readonly planoNaoRestaurado?: {
    readonly plano: string;
    readonly razao: string;
    readonly frase: string;
  } | null;
  // ⚠️⚠️ **AQUI ESTAVA `planoActivo`, e era a razão do defeito D-2.** O servidor manda-o
  //    **dentro** de `utilizador`; declarado aqui, `perfil.planoActivo` era sempre `undefined` e o
  //    `<select>` mostrava o marcador de posição. 📌 A declaração foi apagada de propósito:
  //    enquanto existisse, o próximo a lê-la voltava a apanhar `undefined` sem aviso.
}

// ============================================================================
// ⚠️⚠️ O SELECTOR DE PLANO — 📗 «um mapa por plano» (2026-08-19)
// ============================================================================
//
// 📌 **Componente próprio e exportado** para haver **uma** implementação quando ele passar
// também para a barra de topo. ⚠️ `Mapa.tsx` está fora do âmbito deste trabalho, logo a barra de
// topo fica declarada e não feita: duas cópias do selector divergiriam, e a que divergisse seria a
// que ninguém corre.
// ============================================================================

/** Trocar de plano. Devolve o erro, ou `null` se correu bem. */
export async function trocarDePlano(plano: string): Promise<string | null> {
  // ⚠️ D21 · pela formulação PARTILHADA (`rede.ts`). 🔴 Antes, uma falha de REDE fazia esta
  //    promessa REJEITAR, e quem chama faz `const mal = await trocarDePlano(...)` sem `catch`:
  //    a rejeição subia como erro não tratado, o `finally` largava o `aTrocar` e o selector
  //    ficava a mostrar o plano NOVO sobre uma sessão que continuava no ANTIGO.
  // 📌 E isso não é cosmético: o plano da sessão decide o que cada consulta vê (ADR-002).
  const res = await escreverNaRede('/api/auth/sessao/plano', { plano });
  return res.tipo === 'ok' ? null : res.frase;
}

export function SelectorDePlano({
  planos,
  planoActivo,
  naoRestaurado = null,
}: {
  readonly planos: readonly { readonly plano: string; readonly descricao: string }[];
  readonly planoActivo: string | null;
  /**
   * ⚠️ A frase do servidor quando a memória do plano existia e não valeu (079). Fica **ao
   * lado do selector** porque é aqui que a pessoa vai reparar que está sem plano.
   */
  readonly naoRestaurado?: { readonly frase: string } | null;
}) {
  const [aTrocar, definirATrocar] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  // ⚠️ **Um plano só não é uma escolha.** Um selector com uma opção convida a um gesto que não
  //    faz nada — e esconde a informação que importa (qual é o plano), que fica no texto ao lado.
  if (planos.length === 0) return null;

  const escolher = async (plano: string): Promise<void> => {
    if (plano === '' || plano === planoActivo) return;
    definirATrocar(true);
    definirErro(null);
    const mal = await trocarDePlano(plano);
    if (mal !== null) {
      definirErro(mal);
      definirATrocar(false);
      return;
    }
    // ⚠️⚠️ **RECARREGA A PÁGINA, e é o mesmo argumento do «Sair».**
    //
    //   📌 A alternativa era recarregar o mapa, os pedidos, as consultas, os painéis abertos e
    //   as caches um a um — e o que ficasse esquecido era **dados de outro plano** a continuar no
    //   ecrã. ⚠️ Recarregar não pode esquecer nada, e é o único caminho em que isso é garantido.
    //
    //   ⚠️ **Hoje nada filtra** (§14 passo 1), logo o recarregamento não muda o que se vê. Fica
    //   assim de propósito: quando o filtro entrar, este gesto já está correcto.
    window.location.reload();
  };

  return (
    <div className="sessao-plano">
      <label>
        <span>plano activo</span>{' '}
        <select
          value={planoActivo ?? ''}
          disabled={aTrocar}
          onChange={(e) => void escolher(e.currentTarget.value)}
          aria-label="plano activo desta sessão"
        >
          {/* ⚠️⚠️ **A opção vazia existe SÓ quando não há escolha feita.** 📌 Mantê-la sempre
              dava a impressão de que «nenhum plano» é um estado a que se pode voltar — e não é: o
              que se pode é escolher outro. Um `<select>` cujo `value` não existe entre as
              `<option>` mostra a primeira, e este projecto já pagou esse defeito uma vez. */}
          {planoActivo === null || planoActivo === '' ? (
            <option value="">— escolher —</option>
          ) : null}
          {planos.map((pl) => (
            <option key={pl.plano} value={pl.plano}>
              {pl.descricao}
            </option>
          ))}
        </select>
      </label>
      {planoActivo === null || planoActivo === '' ? (
        <span className="sessao-plano-aviso">
          ⚠️ ainda não escolheu um plano
        </span>
      ) : null}
      {/* ⚠️⚠️ **A RAZÃO, quando havia memória e ela não valeu** (079). 📌 Fica **ao lado** do
          aviso e não em vez dele: o aviso diz o ESTADO («está sem plano») e isto diz a CAUSA
          («o que estava a ver foi arquivado»). Substituir um pelo outro tirava a metade que
          diz o que fazer agora, e trocar a causa por silêncio era o defeito que o cliente
          apanhou. ⚠️ Só aparece sem plano escolhido: com um plano activo, uma frase sobre a
          memória lia-se como aviso sobre ESTE plano. */}
      {naoRestaurado !== null && (planoActivo === null || planoActivo === '') ? (
        <span className="sessao-plano-aviso" role="status">
          {naoRestaurado.frase}
        </span>
      ) : null}
      {erro !== null ? <span className="sessao-plano-erro">⚠️ {erro}</span> : null}
    </div>
  );
}

/**
 * O que cada capacidade permite, em português corrente.
 *
 * ⚠️ **Os nomes internos NÃO vão ao ecrã.** `escrever_plano` é um identificador do domínio;
 * «reservar, efectivar e mover serviços» é o que a pessoa reconhece do que faz todos os dias.
 * 📌 É a mesma lição de «é do plano P-CONT»: um código de sistema numa frase para uma pessoa
 * é uma pergunta que alguém vai ter de fazer.
 */
const O_QUE_PERMITE: Readonly<Record<string, string>> = {
  ler: 'ver o mapa, os pedidos e as consultas',
  escrever_plano: 'reservar, efectivar, mover serviços e marcar indisponibilidades',
  escrever_pedido: 'criar e alterar pedidos',
  configurar: 'alterar limites legais, custos e pesos',
  ratificar: 'ratificar divergências pendentes',
  ver_auditoria: 'ler o log de auditoria',
  gerir_contas: 'criar contas e atribuir papéis',
  processar_saida: 'forçar o envio das notificações',
  gerir_entidades: 'registar viaturas, motoristas, clientes, itinerários e oficinas',
};

/** `proprietario` → «proprietário». Os papéis da fonte §5.2, escritos como ela os escreve. */
const PAPEL_NO_PLANO: Readonly<Record<string, string>> = {
  proprietario: 'proprietário',
  membro: 'membro',
  convidado: 'convidado (só leitura)',
};

export function Sessao({ fechar }: { readonly fechar: () => void }) {
  const { posicao, aoPressionar, aArrastar } = useArrastavel(() =>
    // Cascata das janelas de topo (ver `cantoInicial` em janela.ts): 4.º degrau à direita.
    cantoInicial(
      'direita',
      { largura: 420, altura: 520 },
      { largura: window.innerWidth, altura: window.innerHeight },
      160,
      208,
    ),
  );
  useEscape(fechar);
  const [perfil, definirPerfil] = useState<PerfilDaSessao | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [aSair, definirASair] = useState(false);
  /**
   * ⚠️ O contador do «Actualizar». Esta janela era a unica das onze sem lever nenhum: o
   * `fetch` estava embutido num efeito com `[]`, logo nao havia como o voltar a correr sem
   * fechar e reabrir. 📗 *«em todas as abas tem de ter a opcao actualizar»* — e as
   * permissoes que ela mostra sao exactamente o que muda **por fora**, quando um
   * administrador as altera noutro ecra.
   */
  const [recargas, definirRecargas] = useState(0);

  useEffect(() => {
    const abortar = new AbortController();
    fetch('/api/auth/sessao', { signal: abortar.signal })
      .then(async (r) => definirPerfil((await r.json()) as PerfilDaSessao))
      .catch((e: unknown) => {
        if (!abortar.signal.aborted) definirErro(e instanceof Error ? e.message : String(e));
      });
    return () => abortar.abort();
  }, [recargas]);

  /**
   * Sair.
   *
   * ⚠️ **Recarrega a página inteira em vez de limpar estado.** 📌 A alternativa era apagar o
   * mapa, os pedidos, as consultas e os painéis um a um — e o que ficasse esquecido era
   * **dados do plano de uma sessão terminada** a continuar no ecrã. Recarregar não pode
   * esquecer nada, e é o único caminho em que isso é garantido.
   */
  const sair = useCallback(async () => {
    definirASair(true);
    try {
      await fetch('/api/auth/sair', { method: 'POST' });
    } catch {
      // ⚠️⚠️ **ISENÇÃO DE MÉRITO (D21), e não uma falha por tratar.** Aqui não há onde escrever
      //    a frase nem para quem: o `finally` recarrega a página, logo o ecrã que mostraria o
      //    erro deixa de existir no instante seguinte. 📌 E a garantia deste gesto **é** a
      //    recarga — ver a nota acima: apagar o estado à mão deixava dados de uma sessão
      //    terminada no ecrã. Manter a recarga é o comportamento seguro nas duas pontas.
      // ⚠️ **O que fica, declarado:** se o `POST` não chegar, o testemunho continua válido e a
      //    pessoa reaparece dentro da aplicação. É **visível** (ela vê que não saiu), ao
      //    contrário de uma escrita silenciosa — e a saída seria não recarregar, que troca este
      //    caso por dados de sessão velha no ecrã.
    } finally {
      window.location.reload();
    }
  }, []);

  const u = perfil?.utilizador ?? null;

  return (
    <div
      className={`janela janela--sessao${aArrastar ? ' janela--a-arrastar' : ''}`}
      role="dialog"
      aria-label="Sessão e permissões"
      style={{ left: posicao.x, top: posicao.y }}
    >
      <div className="janela-cab faixa-quebra" onPointerDown={aoPressionar}>
        <strong className="menu-titulo">A minha sessão</strong>
        <BotaoActualizar
          actualizar={() => definirRecargas((n) => n + 1)}
          assinatura={() => JSON.stringify(perfil)}
        />
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>

      <div className="janela-corpo">
        {erro !== null ? (
          <p className="pedidos-erro" role="alert">
            {erro}
          </p>
        ) : perfil === null ? (
          <p className="pedidos-vazio">a carregar…</p>
        ) : u === null ? (
          <p className="pedidos-vazio">Não há sessão. Recarregue a página para entrar.</p>
        ) : (
          <>
            <dl className="sessao-campos">
              <div>
                <dt>quem</dt>
                <dd>
                  <strong>{u.nome}</strong>
                  {u.email === null ? null : <span className="sessao-email"> {u.email}</span>}
                </dd>
              </div>
              <div>
                <dt>papel na plataforma</dt>
                <dd>
                  {u.papel === 'superadmin' ? 'superadmin — gere a plataforma' : u.papel}
                </dd>
              </div>
              {/* ⚠️ **O MÉTODO DE ENTRADA vai ao ecrã, e não é decoração.** O ADR-004 §2.1-bis
                  abriu duas portas — SSO Microsoft e credenciais locais — e a segurança do
                  sistema é a da **porta mais fraca**. Quem lê o log tem de poder distinguir
                  as duas (A-6), e quem está a usar o sistema também. */}
              {u.metodoEntrada === undefined || u.metodoEntrada === null ? null : (
                <div>
                  <dt>entrou por</dt>
                  <dd>
                    {u.metodoEntrada === 'credenciais'
                      ? 'credenciais locais, com TOTP'
                      : u.metodoEntrada}
                  </dd>
                </div>
              )}
            </dl>

            {/* ⚠️⚠️ **MUDAR A PRÓPRIA SENHA** (2026-08-18) — e sem isto a senha temporária de uma
                conta criada pelo superadmin tornava-se permanente. */}
            <MudarSenha temporaria={perfil.senhaTemporaria === true} />

            {/* ⚠️⚠️ **AS PERMISSÕES POR PLANO, e não uma lista global.** Um utilizador pode ser
                proprietário de um plano e convidado noutro (ADR-002) — mostrar só «utilizador»
                escondia exactamente a parte que decide o que ele consegue fazer, e onde. */}
            <p className="sessao-rot">o que posso fazer, plano a plano</p>
            {/* ⚠️⚠️ O selector, acima da lista: é a escolha, e a lista é a explicação dela. */}
            {/* ⚠️⚠️ **`perfil.utilizador?.planoActivo`, e não `perfil.planoActivo`** (D-2).
                📌 O servidor põe-no dentro de `utilizador`; lido do topo era sempre
                `undefined`, e o selector abria vazio mesmo com um plano escolhido. */}
            <SelectorDePlano
              planos={perfil.planos}
              planoActivo={perfil.utilizador?.planoActivo ?? null}
              naoRestaurado={perfil.planoNaoRestaurado ?? null}
            />
            {perfil.planos.length === 0 ? (
              <p className="pedidos-vazio">
                Sem acesso a nenhum plano. Um superadmin tem de lhe atribuir um — até lá, o
                sistema recusa todas as operações.
              </p>
            ) : (
              <ul className="sessao-planos">
                {perfil.planos.map((pl) => (
                  <li key={pl.plano}>
                    <strong>{pl.descricao}</strong>{' '}
                    <span className="sessao-papel">
                      {PAPEL_NO_PLANO[pl.papel] ?? pl.papel}
                    </span>
                    <ul className="sessao-caps">
                      {pl.capacidades.map((c) => (
                        // Em português corrente: ver `O_QUE_PERMITE`.
                        <li key={c}>{O_QUE_PERMITE[c] ?? c}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}

            {/* ⚠️⚠️ O IDIOMA, desactivado E com a razão — ver o cabeçalho deste ficheiro. */}
            <p className="sessao-rot">idioma</p>
            <label className="sessao-idioma">
              <select
                disabled
                value="pt"
                aria-label="idioma da aplicação"
                title="ainda não é possível mudar: o texto está escrito directamente nos componentes (~1 400 cadeias) e traduzir exige extraí-lo primeiro. Foi registado como trabalho por fazer."
              >
                <option value="pt">Português (Portugal)</option>
              </select>
              <span className="sessao-nota">
                Só português, por agora. Mudar de idioma exige extrair o texto dos componentes
                — está registado como trabalho por fazer, não como interruptor.
              </span>
            </label>

            <div className="sessao-acoes">
              <button
                type="button"
                className="btn btn--peq btn--principal"
                onClick={() => void sair()}
                disabled={aSair}
              >
                {aSair ? 'a sair…' : 'Sair'}
              </button>
              {/* ⚠️ Diz o que «sair» faz de facto: termina a sessão **no servidor**. Um
                  «sair» que só limpasse o ecrã deixava o testemunho válido durante 12 h. */}
              <span className="sessao-nota">
                Termina a sessão no servidor e recarrega a aplicação.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
